/**
 * Mounts Swiper for snippets/custom-product-media.liquid's gallery + thumbnail rail.
 *
 * This exists because of a real bug in the shared implementation (assets/global.js's
 * sliderInit/subSliderInit, wired up sitewide by update-slider.js): after creating the
 * main gallery Swiper with `thumbs: { swiper: thumbsSwiper }` — which already makes
 * Swiper's own Thumbs module keep the active thumbnail correctly positioned inside the
 * visible window (direction-aware) on every slide change — it ALSO adds a manual
 * `slideChangeTransitionStart` handler that calls `thumbsSwiper.slideTo(this.activeIndex)`.
 * That second call fights the built-in sync with a naive, non-windowed scroll position.
 * On a product with more images than fit on screen at once (slidesPerView: 5 below),
 * that's what leaves the thumbnail for the image actually being viewed scrolled out of
 * view when navigating with the arrows. The fix is just not adding that override —
 * Thumbs already does this correctly on its own.
 */
;(function () {
	// snippets/product-thumbnail.liquid (shared, unedited) renders video/model slides
	// behind a click-to-play poster by design — assets/global.js's DeferredMedia only
	// swaps the real <video> in once its poster button is clicked. This section wants
	// gallery video to just autoplay/loop on its own instead (it already has its own
	// "loop" merchant setting), so this calls the same loadContent() a click would
	// trigger, but automatically, for whichever slide is actually active.
	//
	// loadContent() only ever runs its body once (it's guarded by `if (!loaded)`), and
	// the mainSwiper's own slideChange handler below calls window.pauseAllMedia() (shared,
	// unedited) on every single slide change — including arriving back at a video slide
	// that's already loaded from a previous visit. That pauses the video, and since
	// loadContent() no-ops on an already-loaded element, nothing ever un-pauses it —
	// leaving it stuck paused (and, since preload is "none", showing nothing) until the
	// page reloads. Resuming with .play() when it's already loaded closes that gap.
	function autoLoadDeferredMedia(slide) {
		var deferredMedia = slide && slide.querySelector('deferred-media')
		if (!deferredMedia) return
		if (deferredMedia.getAttribute('loaded')) {
			var video = deferredMedia.querySelector('video')
			if (video && video.paused) video.play().catch(function () {})
		} else if (typeof deferredMedia.loadContent === 'function') {
			deferredMedia.loadContent()
		}
	}

	function initCustomProductMediaSwipers() {
		var subLists = document.querySelectorAll('.custom-product__media-sublist')
		if (!subLists.length) return

		subLists.forEach(function (elem) {
			if (elem.swiper && elem.swiper.destroy) elem.swiper.destroy(true, true)

			// 'vertical' (default): a 4-thumbnail rail beside the image on desktop — a
			// real breakpoint override below, since slidesPerView/direction actually
			// change at 990px. 'horizontal': 5 across at every width — no breakpoint
			// needed, since desktop uses the exact same slidesPerView/direction as
			// mobile, just a wider spaceBetween (12, to match assets/section-custom-
			// product.css's fixed-width rail: 5 x 9rem + 4 x 1.2rem gaps = 49.8rem,
			// which is what makes each slide resolve to 9rem at that width) — applied
			// directly below instead of through a breakpoint that would just restate
			// identical slidesPerView/direction values for no reason.
			var layout = elem.dataset.thumbnailLayout || 'vertical'
			var baseSpaceBetween = layout === 'horizontal' ? 12 : 8

			var breakpoints = {}
			if (layout === 'vertical') {
				// slidesPerView is 4, and spaceBetween is 12, so 4 of the 9rem square
				// (see assets/section-custom-product.css) thumbnails fit together with
				// real gaps between them, instead of 5 squeezed thin against each
				// other. That CSS file also grows the rail's own fixed height to fit:
				// 4 x 9rem + 3 x 1.2rem (12px) gaps = 39.6rem.
				breakpoints[990] = {
					slidesPerView: 4,
					spaceBetween: 12,
					direction: 'vertical',
					autoHeight: false,
				}
			}

			new Swiper(elem, {
				slidesPerView: 5,
				spaceBetween: baseSpaceBetween,
				direction: 'horizontal',
				freeMode: false,
				watchSlidesProgress: true,
				roundLengths: true,
				autoHeight: false,
				on: {
					touchEnd: function (s) {
						var range = 5
						var diff = (s.touches.diff = s.isHorizontal()
							? s.touches.currentX - s.touches.startX
							: s.touches.currentY - s.touches.startY)
						if (diff < range || diff > -range) s.allowClick = true
					},
				},
				breakpoints: breakpoints,
			})
		})

		document
			.querySelectorAll('.custom-product__media-list')
			.forEach(function (elem) {
				if (elem.swiper && elem.swiper.destroy) elem.swiper.destroy(true, true)

				var mediaListId = elem.dataset && elem.dataset.customMediaListId
				var mediaSublist = mediaListId
					? Array.from(
							document.querySelectorAll('.custom-product__media-sublist'),
					  ).find(function (subElem) {
							return (
								subElem.dataset &&
								subElem.dataset.customMediaListId === mediaListId &&
								subElem.swiper
							)
					  })
					: null
				var thumbsSwiper = (mediaSublist && mediaSublist.swiper) || null
				var productRoot = elem.closest('.product') || document

				var mainSwiper = new Swiper(elem, {
					slidesPerView: 1,
					spaceBetween: 1,
					autoHeight: true,
					speed: 800,
					navigation: {
						nextEl: productRoot.querySelector(
							'.product__slider-nav .swiper-button-next',
						),
						prevEl: productRoot.querySelector(
							'.product__slider-nav .swiper-button-prev',
						),
					},
					pagination: {
						el: productRoot.querySelector('.product__pagination'),
						type: 'bullets',
						clickable: true,
					},
					thumbs: {
						swiper: thumbsSwiper,
					},
					on: {
						slideChange: function () {
							if (window.pauseAllMedia) window.pauseAllMedia()
							this.params.noSwiping = false
							if (typeof syncPopupSwiperFromMainGallery === 'function') {
								syncPopupSwiperFromMainGallery(this, 0)
							}
						},
						slideChangeTransitionEnd: function () {
							var activeSlide = this.slides[this.activeIndex]
							if (activeSlide && activeSlide.querySelector('model-viewer')) {
								var posterButton = activeSlide.querySelector(
									'.shopify-model-viewer-ui__button--poster',
								)
								if (posterButton) posterButton.removeAttribute('hidden')
							}
							autoLoadDeferredMedia(activeSlide)
						},
						touchStart: function () {
							var activeSlide = this.slides[this.activeIndex]
							var modelViewer =
								activeSlide && activeSlide.querySelector('model-viewer')
							if (modelViewer) {
								if (
									!modelViewer.classList.contains(
										'shopify-model-viewer-ui__disabled',
									)
								) {
									this.params.noSwiping = true
									this.params.noSwipingClass = 'swiper-slide'
								} else {
									this.params.noSwiping = false
								}
							}
						},
					},
				})

				// Autoplays the featured slide's video (if any) on load, same as the
				// slideChangeTransitionEnd handler above does when paging to a later one —
				// without this, the very first video slide would still sit behind its
				// click-to-play poster until a slide change fires at least once.
				autoLoadDeferredMedia(mainSwiper.slides[mainSwiper.activeIndex])

				// Swiper's built-in Thumbs module (the `thumbs: { swiper: thumbsSwiper }`
				// above) keeps click-to-navigate working (clicking a thumbnail still jumps
				// the main gallery to it) but its own automatic scroll-into-view windowing
				// is what's actually broken here: on a rail with more slides than fit on
				// screen at once (slidesPerView: 5, 16 total images in the case that
				// surfaced this), its internal calculation for where to jump the thumb rail
				// to next skips several thumbnails ahead instead of moving the window by
				// one. Rather than guess at fixing that internal math, this replaces it with
				// simple, predictable clamping: whenever the active thumb falls outside the
				// currently visible window, move the window by exactly the amount needed to
				// bring it back into view — one slide at a time as you page through with the
				// arrows, in either direction. Registered via .on() (after construction) so
				// it runs after the Thumbs module's own listener and can correct whatever
				// position that module already moved to.
				if (thumbsSwiper) {
					mainSwiper.on('slideChange', function () {
						var perView = thumbsSwiper.params.slidesPerView
						if (typeof perView !== 'number') return
						var mainIndex = mainSwiper.activeIndex
						var thumbIndex = thumbsSwiper.activeIndex
						if (mainIndex < thumbIndex) {
							thumbsSwiper.slideTo(mainIndex)
						} else if (mainIndex > thumbIndex + perView - 1) {
							thumbsSwiper.slideTo(mainIndex - perView + 1)
						}
					})
				}
			})
	}

	document.addEventListener('shopify:section:load', initCustomProductMediaSwipers)
	initCustomProductMediaSwipers()
})()
